import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@mui/material';
import { useFormikContext } from 'formik';
import store from 'store';
import clearApiCacheAndReloadSW from 'utils/serviceWorkerCleanup';

import TextField from '@common/components/Form/TextField';
import { getRoute } from '@common/constants/CampusRoutes';

import Layout from '../components/Layout';
import schema, { TokenLoginValues } from './schema';

interface PropsType {
	onBack: () => void;
}

function BackButton({ onBack }: PropsType) {
	const { isSubmitting } = useFormikContext();
	return (
		<Button type="button" fullWidth disabled={isSubmitting} onClick={onBack}>
			Back to password login
		</Button>
	);
}

function TokenLogin({ onBack }: PropsType) {
	const navigate = useNavigate();

	const handleSubmit = async (values: TokenLoginValues) => {
		if (new URLSearchParams(window.location.search).get('campusApp') !== '1') return;
		const { token } = await schema.validate(values);
		const pageUrl = window.location.href;
		await clearApiCacheAndReloadSW();
		if (window.location.href !== pageUrl) return;

		// Same session initialization as /auth?token=..., without putting the
		// pasted token in browser history or the address bar.
		store.auth.logout();
		try {
			await store.auth._login({ token }, true);
		} catch {
			store.auth.logout();
			const message = 'Could not log in with this token. Copy a new token and try again.';
			throw Object.assign(new Error(message), { errors: { token: message } });
		}
		navigate(`${getRoute(['NEWS'])}?campusApp=1`, { replace: true });
	};

	return (
		<Layout<TokenLoginValues>
			header="Token login"
			headerText="Paste the user token copied from the admin panel"
			buttonText="Log in with token"
			initialValues={{ token: '' }}
			validationSchema={schema}
			onSubmit={handleSubmit}
			footer={<BackButton onBack={onBack} />}
		>
			<TextField
				name="token"
				label="Token*"
				type="password"
				autoComplete="off"
				inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
			/>
		</Layout>
	);
}

export default TokenLogin;
